'use client';

import { Button, CTA } from '@/components';
import { useDeviceSize } from '@/hooks/useDeviceSize';
import {
  AnimatedNumber,
  Column,
  Divider,
  Element,
  Icon,
  Row,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@vuddy/components';
import { useEffect } from 'react';
import * as styles from '../common/bottom.css';
import { ConnectedVerificationCodeModal } from '../common/ConnectedVerificationCodeModal';
import { CartItem } from './CartItem';
import { ProductSelectDropdown } from './ProductSelectDropdown';
import { SelectedProductOption } from './types';
import { usePhysicalPurchaseLogic } from './usePhysicalPurchaseLogic';

interface PurchaseBottomSheetProps {
  salePageId: number;
  interactive?: boolean;
  onClose?: VoidFunction;
  cartItems: SelectedProductOption[];
  setCartItems: React.Dispatch<React.SetStateAction<SelectedProductOption[]>>;
}

export const PhysicalPurchaseBottomSheet = ({
  salePageId,
  interactive = true,
  onClose,
  cartItems,
  setCartItems,
}: PurchaseBottomSheetProps) => {
  const device = useDeviceSize();

  const {
    itemList,
    isClass,
    localCartItems,
    totalSelectedAmount,
    isAddToCartAllowed,
    verification,
    handleQuantityChange,
    onClickCTA,
    onClickAddToCart,
    addCartItem,
    deleteCartItem,
  } = usePhysicalPurchaseLogic({
    salePageId,
    cartItems,
    setCartItems,
    onClose,
  });

  // 모바일 사이즈가 아닌 경우 닫기
  useEffect(() => {
    if (device && device !== 'mobile') {
      onClose?.();
    }
  }, [device, onClose]);

  return (
    <Column className={styles.wrapper({ interactive })}>
      <Column>
        <Element className={styles.bottomSheetContents}>
          <Element padding="padding_small">
            <Column gap="gap_base">
              <ProductSelectDropdown
                options={itemList}
                cartItems={localCartItems}
                setCartItems={addCartItem}
                hasParentWidth={true}
              />
              {localCartItems.length > 0 &&
                localCartItems.map(cartItem => {
                  const product = itemList?.find(
                    p => p.id === cartItem.productId
                  );
                  if (!product) return null;
                  return (
                    <CartItem
                      key={`${cartItem.productId}-${JSON.stringify(cartItem.optionCategories)}`}
                      item={cartItem}
                      product={product}
                      onQuantityChange={handleQuantityChange}
                      onDelete={deleteCartItem}
                      hasVerificationCode={product.requiresPromotionCode}
                      verificationCodeName={verification.verifiedProductCodes.get(
                        cartItem.productId
                      )}
                      onCodeInputClick={verification.openVerificationModal}
                    />
                  );
                })}
            </Column>
          </Element>
          <Element paddingX="padding_small">
            <Divider />
          </Element>
        </Element>
        <CTA
          direction="vertical"
          position="sticky"
          description={
            <Row justifyContent="space-between">
              <Tooltip
                placement="top-start"
                offsetOptions={{ mainAxis: 10, crossAxis: -5 }}>
                <TooltipTrigger asChild>
                  <Row alignItems="center" gap="gap_xs">
                    <Element typography="bodySmallAccent" color="secondary">
                      총 금액
                    </Element>
                    <Icon
                      name="FILL_INFORMATION_CIRCLE"
                      color="tertiary"
                      size="19px"
                    />
                  </Row>
                </TooltipTrigger>
                <TooltipContent fullWidth>
                  표시된 금액은 최종 금액이 아니며, 주문서에서 최종 금액을
                  확인해 주세요.
                </TooltipContent>
              </Tooltip>
              <AnimatedNumber
                value={totalSelectedAmount}
                typography="titleBase"
                color="primary"
              />
            </Row>
          }>
          {isAddToCartAllowed ? (
            <Row gap="gap_small">
              <Element flex={1}>
                <Button
                  variant="outlinePrimary"
                  size="large"
                  fullWidth
                  onClick={onClickAddToCart}
                  disabled={localCartItems.length === 0}>
                  장바구니 추가
                </Button>
              </Element>
              <Element flex={1}>
                <Button
                  size="large"
                  fullWidth
                  onClick={onClickCTA}
                  disabled={localCartItems.length === 0}>
                  {isClass ? '이용권 구매하기' : '바로 구매'}
                </Button>
              </Element>
            </Row>
          ) : (
            <Button
              size="large"
              fullWidth
              onClick={onClickCTA}
              disabled={localCartItems.length === 0}>
              {isClass ? '이용권 구매하기' : '바로 구매'}
            </Button>
          )}
        </CTA>
      </Column>
      <ConnectedVerificationCodeModal verification={verification} />
    </Column>
  );
};
